
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();

  const footerLinks = {
    products: [
      { name: 'Frozen Fruits & Vegetables', path: '/products/frozen' },
      { name: 'Freeze-Dried Products', path: '/products/freeze-dried' },
      { name: 'Fruit Purees & Concentrates', path: '/products/purees' },
      { name: 'Juices', path: '/products/juices' },
      { name: 'Dried Herbs & Spices', path: '/products/herbs' }
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Our Services', path: '/services' },
      { name: 'Quality & Certificates', path: '/about#quality' },
      { name: 'Sustainability', path: '/about#sustainability' },
      { name: 'Career', path: '/about#career' }
    ],
    support: [
      { name: 'Contact Us', path: '/contact' },
      { name: 'Blog & News', path: '/blog' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Technical Support', path: '/support' },
      { name: 'Export Documentation', path: '/documentation' }
    ]
  };

  const socialLinks = [
    { icon: 'ri-linkedin-fill', href: '#', color: 'hover:text-blue-600' },
    { icon: 'ri-facebook-fill', href: '#', color: 'hover:text-blue-600' },
    { icon: 'ri-instagram-line', href: '#', color: 'hover:text-pink-600' },
    { icon: 'ri-twitter-line', href: '#', color: 'hover:text-blue-400' },
    { icon: 'ri-youtube-line', href: '#', color: 'hover:text-red-600' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="flex items-center mb-6 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-green-600 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-leaf-line text-white text-xl"></i>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-green-400 bg-clip-text text-transparent">
                FrozenExport
              </span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Leading global exporter of premium frozen fruits, vegetables, and freeze-dried products. 
              Quality guaranteed, trusted worldwide since 1995.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 text-gray-400">
                <i className="ri-phone-line text-primary-400"></i>
                <span>+90 212 123 45 67</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <i className="ri-mail-line text-primary-400"></i>
                <span>export@frozenexport.com</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <i className="ri-map-pin-line text-primary-400"></i>
                <span>Istanbul, Turkey</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center transition-all duration-300 ${social.color} hover:bg-gray-700 cursor-pointer`}
                >
                  <i className={`${social.icon} text-lg`}></i>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <i className="ri-product-hunt-line text-primary-400"></i>
              Products
            </h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200 text-sm cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <i className="ri-building-line text-primary-400"></i>
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200 text-sm cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <i className="ri-customer-service-line text-primary-400"></i>
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200 text-sm cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2024 FrozenExport. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <button className="text-gray-400 hover:text-primary-400 transition-colors cursor-pointer">
                Privacy Policy
              </button>
              <button className="text-gray-400 hover:text-primary-400 transition-colors cursor-pointer">
                Terms of Service
              </button>
              <button className="text-gray-400 hover:text-primary-400 transition-colors cursor-pointer">
                Cookie Policy
              </button>
              <a 
                href="https://readdy.ai/?origin=logo" 
                className="text-gray-400 hover:text-primary-400 transition-colors cursor-pointer"
              >
                Made with Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
